import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { expenseApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import ExpenseStatusBadge from '../../components/expense/ExpenseStatusBadge.jsx';
import LineItemsTable from '../../components/expense/LineItemsTable.jsx';
import Skeleton from '../../components/ui/Skeleton.jsx';
import { ArrowLeft, XCircle, Edit, Trash2, Send, AlertCircle, FileText, Download } from 'lucide-react';

export default function ExpenseReportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [report, setReport] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resubmitting, setResubmitting] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const res = await expenseApi.get(id);
        setReport(res.report);
        setLineItems(res.lineItems || []);
        setReceipts(res.receipts || []);
      } catch (err) {
        toast.error(`Failed to load report: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel this expense report?')) return;
    setCancelling(true);
    try {
      await expenseApi.cancel(id);
      toast.success('Expense report cancelled');
      setReport((r) => ({ ...r, status: 'cancelled' }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const handleResubmit = async () => {
    setResubmitting(true);
    try {
      const updated = await expenseApi.submit(id);
      toast.success('Expense report resubmitted for approval');
      setReport(updated);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setResubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this draft? Line items and receipts will be removed too. This cannot be undone.')) return;
    setDeleting(true);
    try {
      await expenseApi.remove(id);
      toast.success('Draft deleted');
      navigate('/expenses');
    } catch (err) {
      toast.error(err.message);
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (!report) {
    return <div className="text-muted-foreground">Expense report not found.</div>;
  }

  const totalSAR = lineItems.reduce((sum, i) => sum + (i.sarAmount || 0), 0);
  const netAmount = totalSAR - (report.lessAdvancesPaid || 0);
  // Prefer the report-level total (set at submit time); fall back to
  // summing the line items for drafts that haven't been submitted yet.
  const totalForeignFeeSAR = report.totalForeignFeeSar != null
    ? (report.totalForeignFeeSar || 0)
    : lineItems.reduce((sum, i) => sum + (i.foreignFeeSar || 0), 0);

  // Pull the rejection note + which approver rejected from the chain
  // snapshot so the requester sees exactly what to fix before
  // resubmitting. The reject endpoint stores this inside the
  // snapshot's step, not on a top-level field.
  let rejectionInfo = null;
  if (report.status === 'rejected' && report.chainSnapshot) {
    try {
      const chain = typeof report.chainSnapshot === 'string'
        ? JSON.parse(report.chainSnapshot)
        : report.chainSnapshot;
      const rejectedStep = Array.isArray(chain) ? chain.find((s) => s.status === 'rejected') : null;
      if (rejectedStep) {
        rejectionInfo = {
          by: rejectedStep.decidedBy || rejectedStep.label || 'Approver',
          note: rejectedStep.note || '',
        };
      }
    } catch {}
  }

  return (
    <div>
      <button
        onClick={() => navigate('/expenses')}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Expenses
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold tracking-tight">{report.claimRef}</h1>
            <ExpenseStatusBadge status={report.status} />
          </div>
          <p className="text-muted-foreground">{report.claimPurpose}</p>
        </div>
        <div className="flex gap-2">
          {report.status === 'draft' && (
            <>
              <Button variant="secondary" onClick={() => navigate(`/expenses/new?edit=${id}`)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                <Trash2 className="h-4 w-4 mr-2" />
                {deleting ? 'Deleting...' : 'Delete Draft'}
              </Button>
            </>
          )}
          {report.status === 'pending' && (
            <Button variant="danger" onClick={handleCancel} disabled={cancelling}>
              <XCircle className="h-4 w-4 mr-2" />
              {cancelling ? 'Cancelling...' : 'Cancel'}
            </Button>
          )}
          {report.status === 'rejected' && (
            <>
              <Button variant="secondary" onClick={() => navigate(`/expenses/new?edit=${id}`)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button onClick={handleResubmit} disabled={resubmitting}>
                <Send className="h-4 w-4 mr-2" />
                {resubmitting ? 'Resubmitting...' : 'Resubmit'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Split child: this report holds the items an approver sent back from
          a larger claim. Show why, per item, and the standard edit/resubmit
          flow (already enabled for rejected reports) takes it from here.
          Gated on status so the banner clears once the employee resubmits.
          Suppressed if there's a chain rejection (i.e. it was resubmitted
          and then rejected by an approver) — that shows the generic banner. */}
      {report.splitFromReportId && report.status === 'rejected' && !rejectionInfo && (
        <Card className="mb-6 border-destructive/40 bg-destructive/5">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-destructive">
                  Items returned for revision{report.splitFromClaimRef ? ` from ${report.splitFromClaimRef}` : ''}
                </div>
                <p className="mt-1 text-sm text-foreground">
                  The rest of that claim was approved and is continuing. These items were split into this
                  claim so you can fix and resubmit just them.
                </p>
                {report.rejectionReason && (
                  <p className="mt-2 text-sm text-foreground"><span className="text-muted-foreground">Reason: </span>{report.rejectionReason}</p>
                )}
                {lineItems.some((it) => it.rejectionNote) && (
                  <ul className="mt-2 space-y-1">
                    {lineItems.filter((it) => it.rejectionNote).map((it) => (
                      <li key={it.rowKey} className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{it.vendorName || it.description || it.expenseTypeId || 'Item'}:</span> {it.rejectionNote}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-2 text-xs text-muted-foreground">
                  Edit the line items / receipts to address the feedback, then click Resubmit to send these back through approval.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Parent: some items were split off into a new claim for revision. */}
      {report.amendedToReportId && (
        <Card className="mb-6 border-profun-blue/30 bg-profun-blue/5">
          <CardContent className="flex items-start gap-3 py-4">
            <AlertCircle className="h-5 w-5 text-profun-blue shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-foreground">Approved with amendments</div>
              <p className="mt-1 text-sm text-muted-foreground">
                Some items were sent back for revision as a separate claim
                {report.amendedToClaimRef ? ` (${report.amendedToClaimRef})` : ''}. The items shown here were
                approved and are continuing through the approval chain.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {rejectionInfo && (
        <Card className="mb-6 border-destructive/40 bg-destructive/5">
          <CardContent className="flex items-start gap-3 py-4">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-destructive">Rejected by {rejectionInfo.by}</div>
              {rejectionInfo.note && (
                <p className="mt-1 text-sm text-foreground">{rejectionInfo.note}</p>
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                Edit the report or its line items / receipts to address the feedback, then click Resubmit to send it back through approval.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header details */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Report Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Location</div>
              <div className="font-medium">{report.officeLocation || '\u2014'}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Position</div>
              <div className="font-medium">{report.position || '\u2014'}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Project</div>
              <div className="font-medium">{report.project || 'N/A'}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Client</div>
              <div className="font-medium">{report.client || 'N/A'}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Created</div>
              <div className="font-medium">
                {report.createdAt ? new Date(report.createdAt).toLocaleDateString() : '\u2014'}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Submitted</div>
              <div className="font-medium">
                {report.submittedAt ? new Date(report.submittedAt).toLocaleDateString() : '\u2014'}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Mileage Rate</div>
              <div className="font-medium">{report.mileageRate || '\u2014'}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Claim Ref</div>
              <div className="font-mono font-semibold text-profun-cyan">{report.claimRef}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Line items */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Expense Items ({lineItems.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <LineItemsTable items={lineItems} readOnly />
        </CardContent>
      </Card>

      {/* Receipts — shown independently of line items so recovered / not-yet-
          filled-in receipts are visible here instead of the page looking empty. */}
      {receipts.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Receipts ({receipts.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {receipts.map((r) => {
                const isPdf = /pdf/i.test(r.mimeType || '') || /\.pdf$/i.test(r.fileName || '');
                return (
                  <a
                    key={r.rowKey || r.itemKey}
                    href={r.url || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card hover:border-primary/40 transition-colors"
                    title={r.fileName || 'Receipt'}
                  >
                    <div className="aspect-[4/3] bg-muted/30 flex items-center justify-center overflow-hidden">
                      {r.url && !isPdf ? (
                        <img src={r.url} alt={r.fileName || 'Receipt'} className="h-full w-full object-cover" />
                      ) : (
                        <FileText className="h-8 w-8 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-muted-foreground">
                      <Download className="h-3 w-3 shrink-0" />
                      <span className="truncate">{r.fileName || 'Receipt'}</span>
                    </div>
                  </a>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Totals */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total Claimed (SAR)</span>
              <span className="font-mono font-semibold">{totalSAR.toFixed(2)}</span>
            </div>
            {totalForeignFeeSAR > 0 && (
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Foreign transaction fees (3%, included above)</span>
                <span className="font-mono">{totalForeignFeeSAR.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Less Advances Paid</span>
              <span className="font-mono">{(report.lessAdvancesPaid || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-base font-bold pt-3 border-t border-border">
              <span>Net Amount (SAR)</span>
              <span className="text-profun-cyan font-mono">{netAmount.toFixed(2)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Approval chain */}
      {report.chainSnapshot && report.chainSnapshot !== '[]' && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Approval Chain</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(typeof report.chainSnapshot === 'string'
                ? JSON.parse(report.chainSnapshot)
                : report.chainSnapshot
              ).map((step, i) => {
                const isDecided = step.status === 'approved' || step.status === 'rejected';
                return (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        step.status === 'approved'
                          ? 'bg-profun-teal/20 text-profun-teal'
                          : step.status === 'rejected'
                            ? 'bg-profun-pink/20 text-profun-pink'
                            : i === report.currentStep
                              ? 'bg-profun-blue/20 text-profun-blue'
                              : 'bg-muted/50 text-muted-foreground'
                      }`}
                    >
                      {i + 1}
                    </div>
                    <div>
                      <span className="font-medium">{step.label || step.approverType || `Step ${i + 1}`}</span>
                      {isDecided && step.decidedBy && (
                        <span className="text-muted-foreground ml-2">
                          — {step.decidedBy} ({step.status})
                          {step.decidedAt ? ` · ${new Date(step.decidedAt).toLocaleString()}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
